/**
 * PA Delinquent-Collector Grouping — frontend mirror
 * ====================================================
 *
 * Mirrors the backend predicate in
 * `backend/services/pa_delinquent_grouping.py` so the Send Mail-Away
 * banner can detect grouping eligibility client-side and surface the
 * [Combine] / [Send separately] toggle without an extra round trip.
 *
 * Eligibility (all three must hold, post Feb 9, 2026):
 *   (i)   order.property_state === "PA"
 *   (ii)  ≥ 2 of the operator's selected jurisdictions point to the
 *         SAME `delinquent_collector_id`
 *   (iii) `same_as_current_payee` is False on ≥ 1 of the shared rows
 *
 * The original spec also gated on a delinquent installment existing,
 * but that inverts the actual PA workflow — operators send this MLC
 * to OBTAIN delinquent information. The explicit Tax Claim Bureau
 * assignment IS the intent signal.
 *
 * Cross-order grouping is OUT OF SCOPE.
 *
 * NOTE: This file deliberately does NOT mirror the server-side
 * env-var feature flag — the server is authoritative. If the env flag
 * is OFF on the backend, the server will silently ignore any
 * `delinquent_groups` payload and the legacy 1-per-jurisdiction
 * behaviour kicks in. The frontend never tries to short-circuit on
 * its own.
 */

const PA_STATES = new Set(['PA', 'PENNSYLVANIA']);

const DELINQUENT_PAY_STATUSES = new Set([
  'delinquent',
  'partially delinquent',
  'paid with penalty',
]);

function _state(order) {
  const raw =
    order?.property?.state ||
    order?.property?.property_state ||
    order?.property_state ||
    order?.state ||
    '';
  return String(raw).trim().toUpperCase();
}

function _isPa(order) {
  return PA_STATES.has(_state(order));
}

function _iterJurisdictions(order) {
  const out = [];
  // parcels[].jurisdictions[] on the order root
  for (const parcel of order?.parcels || []) {
    for (const j of parcel?.jurisdictions || []) out.push(j);
  }
  // tax_certificate.parcels[].jurisdictions[]
  const cert = order?.tax_certificate || order?.certificate || {};
  for (const parcel of cert?.parcels || []) {
    for (const j of parcel?.jurisdictions || []) out.push(j);
  }
  // legacy root-level jurisdictions on cert + order
  for (const j of cert?.jurisdictions || []) out.push(j);
  for (const j of order?.jurisdictions || []) out.push(j);
  return out;
}

function _jurId(j) {
  // Feb 2026 P0 — return the MASTER jurisdiction id (from the
  // Jurisdiction Directory) first, then the cert-row id. Callers
  // such as `MailAwayModal` build `selectedJurisdictionIds` from
  // `j.jurisdiction_id` (master) so the bucketing key must match.
  // Falling back to `j.id` preserves legacy callers that only
  // surface the cert-row UUID.
  return j?.jurisdiction_id || j?.id || null;
}

function _isSameAsCurrentPayee(j) {
  // Feb 2026 P0 — accept both the snapshot field name (set by the
  // directory enrichment helper) and the cert-form field name
  // (`delinquent_same_as_current`) so the engine works regardless of
  // whether the data came from the GET /tax-cert response (cert form
  // field) or the live order doc enriched in /mailaway/send.
  let v = j?.same_as_current_payee;
  if (v === null || v === undefined) {
    v = j?.delinquent_same_as_current;
  }
  if (v === null || v === undefined) return true;
  return Boolean(v);
}

function _delinquentCollectorId(j) {
  if (_isSameAsCurrentPayee(j)) return null;
  return j?.delinquent_collector_id || null;
}

function _isDelinquentRow(inst) {
  const raw = String(inst?.pay_status || inst?.status || '').trim().toLowerCase();
  return DELINQUENT_PAY_STATUSES.has(raw);
}

function _jurHasDelinquentInstallment(j) {
  return (j?.installments || []).some(_isDelinquentRow);
}

function _delinquentCollectorDisplayName(j) {
  return (
    j?.delinquent_collector_name ||
    j?.delinquent_payable_to_name ||
    j?.delinquent_make_checks_payable_to ||
    j?.delinquent_collector?.display_name ||
    j?.delinquent_collector?.legal_name ||
    null
  );
}

/**
 * Compute the grouping plan for the operator's current jurisdiction
 * selection. Returns `null` when no grouping applies — callers can
 * use the falsy return as the "skip the banner" signal.
 */
export function computeDelinquentGroups(order, selectedJurisdictionIds) {
  if (!_isPa(order)) return null;

  const selected = new Set((selectedJurisdictionIds || []).filter(Boolean).map(String));
  if (selected.size < 2) return null;

  // Bucket by delinquent_collector_id, de-duplicating jurisdictions
  // that appear in both the parcels[] and cert-snapshot shapes.
  const bucket = new Map(); // coll_id -> Map<jur_id, jur>
  for (const j of _iterJurisdictions(order)) {
    const jid = _jurId(j);
    if (!jid || !selected.has(String(jid))) continue;
    const collId = _delinquentCollectorId(j);
    if (!collId) continue;
    if (!bucket.has(collId)) bucket.set(collId, new Map());
    bucket.get(collId).set(String(jid), j);
  }

  const groups = [];
  for (const [collId, byId] of bucket) {
    const jurs = Array.from(byId.values());
    if (jurs.length < 2) continue;
    // No delinquent-installment gate — operators send this MLC to
    // OBTAIN delinquent information from the Tax Claim Bureau.

    const jursSorted = jurs.slice().sort((a, b) =>
      String(_jurId(a)).localeCompare(String(_jurId(b)))
    );
    const displayName = jursSorted.map(_delinquentCollectorDisplayName).find(Boolean) || null;

    groups.push({
      delinquent_collector_id: collId,
      delinquent_collector_name: displayName,
      jurisdiction_ids: jursSorted.map((j) => String(_jurId(j))),
      jurisdiction_names: jursSorted.map((j) => j?.jurisdiction_name || j?.name || null),
      jurisdiction_types: jursSorted.map((j) => j?.jurisdiction_type || j?.type || null),
    });
  }

  if (groups.length === 0) return null;
  return { groups };
}

/**
 * Convenience boolean — mirrors the Python `is_pa_delinquent_grouping_eligible`.
 */
export function isPaDelinquentGroupingEligible(order, selectedJurisdictionIds) {
  return computeDelinquentGroups(order, selectedJurisdictionIds) !== null;
}
